/**
 * 사진 동기화 콜백 팩토리
 * useAutoSync, SyncQueueContext 양쪽에서 공유
 */

import type { ApolloClient } from "@apollo/client";
import { createLogger, MeasureType } from "@ict/shared";
import {
  MACHINE_DOCUMENT_PICTURES_QUERY,
  MACHINE_DOCUMENT_VERSION_QUERY,
  generateClientMutationId,
  type UpdateMachineDocumentPicturesData,
  type UpdateMachineDocumentPicturesVariables,
} from "@/lib/graphql/equipment";
import { MACHINE_MEASURES_QUERY, type MachineMeasuresData } from "@/lib/graphql/measures";
import type { PhotoEtcUpdateCallback, MeasurePhotoUpdateCallback } from "@/lib/db/sync";

const logger = createLogger({ module: "PhotoCallbacks" });

// Apollo useMutation이 반환하는 함수의 최소 타입
type UpdatePicturesMutationFn = (options: {
  variables: UpdateMachineDocumentPicturesVariables;
}) => Promise<{ data?: UpdateMachineDocumentPicturesData | null }>;

type UpdateMeasureMutationFn = (options: {
  variables: { input: Record<string, unknown> };
}) => Promise<{
  data?: { updateMeasure?: { success: boolean; error?: string; newVersion?: number } } | null;
}>;

/**
 * 문서 버전 조회 (VERSION_CONFLICT 해결용)
 */
export async function fetchDocumentVersion(
  apolloClient: ApolloClient<unknown>,
  projectId: string,
  docKey: string,
  machineId: string
): Promise<number | null> {
  try {
    const { data } = await apolloClient.query({
      query: MACHINE_DOCUMENT_VERSION_QUERY,
      variables: { projectId, docKey, machineId },
      fetchPolicy: "network-only",
    });
    return data?.machineDocument?.version ?? null;
  } catch (error) {
    logger.error("버전 조회 실패:", error);
    return null;
  }
}

/**
 * photoEtc 업로드 후 pictures 배열 업데이트 콜백 생성
 */
export function createPhotoEtcUploadedHandler(
  apolloClient: ApolloClient<unknown>,
  updatePicturesFn: UpdatePicturesMutationFn
): PhotoEtcUpdateCallback {
  return async (params) => {
    try {
      const { data } = await apolloClient.query({
        query: MACHINE_DOCUMENT_PICTURES_QUERY,
        variables: {
          projectId: params.projectId,
          docKey: params.docKey,
          machineId: params.machineId,
        },
        fetchPolicy: "network-only",
      });

      const currentDoc = data?.machineDocument;
      if (!currentDoc) {
        logger.error("MachineDocument를 찾을 수 없음");
        return false;
      }

      const currentPictures = (currentDoc.pictures as string[]) || [];
      const currentVersion = currentDoc.version ?? 0;

      if (currentPictures.includes(params.uploadedUrl)) {
        logger.info("이미 pictures에 포함된 URL - 스킵");
        return true;
      }

      const newPictures = [...currentPictures, params.uploadedUrl];

      const result = await updatePicturesFn({
        variables: {
          input: {
            projectId: params.projectId,
            docKey: params.docKey,
            machineId: params.machineId,
            pictures: newPictures,
            expectedVersion: currentVersion,
            clientMutationId: generateClientMutationId(),
          },
        },
      });

      const response = result.data?.updateMachineDocumentPictures;
      if (response?.success) {
        logger.info("pictures 배열 업데이트 성공");
        return true;
      }

      logger.error("pictures 업데이트 실패:", response?.error);
      return false;
    } catch (error) {
      logger.error("photoEtc pictures 업데이트 오류:", error);
      return false;
    }
  };
}

/**
 * measurePhoto 업로드 후 measure photo URL 업데이트 콜백 생성
 */
export function createMeasurePhotoUploadedHandler(
  apolloClient: ApolloClient<unknown>,
  updateMeasureFn: UpdateMeasureMutationFn
): MeasurePhotoUpdateCallback {
  return async (params) => {
    try {
      // 1. 서버에서 현재 measure 데이터 조회
      const { data } = await apolloClient.query<MachineMeasuresData>({
        query: MACHINE_MEASURES_QUERY,
        variables: {
          projectId: params.projectId,
          docKey: params.docKey,
          machineId: params.machineId,
        },
        fetchPolicy: "network-only",
      });

      if (!data?.machineMeasures) {
        logger.error("계측 데이터를 찾을 수 없음");
        return false;
      }

      // 2. measureType enum 키 → 쿼리 결과 필드명 변환
      const fieldName =
        MeasureType[params.measureType as keyof typeof MeasureType] ?? params.measureType;
      const measureArray = data.machineMeasures[fieldName as keyof typeof data.machineMeasures] as
        | Record<string, unknown>[]
        | undefined;

      if (!measureArray || params.measureIndex >= measureArray.length) {
        // 삭제된 계측의 사진일 수 있으므로 warn 레벨로 기록 (#458)
        logger.warn("계측 항목을 찾을 수 없음 (삭제된 계측일 수 있음)", {
          measureType: params.measureType,
          measureIndex: params.measureIndex,
          arrayLength: measureArray?.length,
        });
        return false;
      }

      // Apollo 쿼리 결과에서 __typename 제거
      const rawMeasure = measureArray[params.measureIndex];
      const { __typename: _t, photo: rawPhoto, ...measureFields } = rawMeasure;
      const { __typename: _pt, ...photoFields } = (rawPhoto as Record<string, unknown>) ?? {};

      const photoFieldName = params.slot === "check" ? "checkUrl" : "resultUrl";

      // 3. 서버 문서 버전 조회 (낙관적 동시성 제어)
      const serverVersion = await fetchDocumentVersion(
        apolloClient,
        params.projectId,
        params.docKey,
        params.machineId
      );

      // 4. updateMeasure mutation으로 photo URL 업데이트
      const result = await updateMeasureFn({
        variables: {
          input: {
            projectId: params.projectId,
            docKey: params.docKey,
            machineId: params.machineId,
            measureType: params.measureType,
            measureIndex: params.measureIndex,
            ...(serverVersion != null ? { expectedVersion: serverVersion } : {}),
            clientMutationId: `measure-photo-${Date.now()}`,
            data: {
              ...measureFields,
              photo: { ...photoFields, [photoFieldName]: params.uploadedUrl },
            },
          },
        },
      });

      const response = result.data?.updateMeasure;
      if (response?.success) {
        logger.info("measure photo URL 업데이트 성공", {
          measureType: params.measureType,
          slot: params.slot,
        });
        return true;
      }

      logger.error("measure photo URL 업데이트 실패:", response?.error);
      return false;
    } catch (error) {
      logger.error("measure photo URL 업데이트 오류:", error);
      return false;
    }
  };
}
